import React from 'react';
import {
  InfoCard,
  InfoCardVariants,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/catalog-client';
import { useAsync } from 'react-use';
import {
  Entity,
  parseEntityRef,
  RELATION_OWNER_OF,
  ResourceEntity,
} from '@backstage/catalog-model';
import {
  createEntityNameColumn,
  EntityFilterTable,
  FILTER_TABLE_OPTIONS,
  FilterableColumn,
  NOTHING_KNOWN,
  orUnknown,
  RelatedEntitiesCard,
  UNKNOWN,
} from '../RelatedEntitiesCard/RelatedEntitiesCard';
import { RELATION_HOSTS } from '@internal/backstage-plugin-catalog-backend-module-rtl/src/processors/realtions';
import {
  buildOrgRows,
  subtreeOf,
} from '../../pages/TeamsPage/DepartmentTreeTable';
import { ARCHIVED_TAG } from '../../input/ArchivedPicker';
import { EntityRefLinkWithVuid } from '../../VuidChip';

export interface HasFilteredResourcesCardProps {
  variant?: InfoCardVariants;
  type: string;
  title: string;
}

/**
 * The environment a resource belongs to is its first tag - a cluster and a
 * slice carry what runs them behind it - see fromResourceToEntity and
 * asClusterEntity in the catalog backend module.
 */
function environmentOf(resource: ResourceEntity): string {
  return resource.metadata.tags?.[0] || UNKNOWN;
}

function clusterTypeOf(resource: ResourceEntity): string {
  const { clusterType } = resource.spec as { clusterType?: string };
  return clusterType || UNKNOWN;
}

function createEnvironmentColumn(): FilterableColumn<ResourceEntity> {
  return {
    title: 'Environment',
    field: 'metadata.tags',
    filterValueOf: environmentOf,
    filterValueNone: NOTHING_KNOWN,
    filterPlaceholder: 'All environments',
    render: resource => orUnknown(environmentOf(resource)),
  };
}

/**
 * The resource types kubernetes is made of: a cluster and the slices cut from
 * one. Both carry what runs them, and a department page gates its Kubernetes
 * tab on either of them being somewhere below it.
 */
export const KUBERNETES_TYPES = ['k8s-cluster', 'k8s-cluster-slice'];

/**
 * The resource types the Cloud Accounts tab of a department is made of: one
 * per cloud a team can hold an account with.
 */
export const CLOUD_TYPES = [
  'gcp-project',
  'aws-account',
  'azure-subscription',
  'oci-account',
];

/**
 * What runs a cluster, as the CMDB calls it: `gke`, `eks`, `rancher`. Nothing
 * else in the row tells them apart - every cluster carries the resource type
 * `k8s-cluster` and they are named alike - which is why the entity keeps it
 * under `spec.clusterType`, next to that resource type. A slice inherits it
 * from the cluster it is cut from and shows it the same way.
 */
function createClusterTypeColumn(): FilterableColumn<ResourceEntity> {
  return {
    title: 'Type',
    field: 'spec.clusterType',
    filterValueOf: clusterTypeOf,
    filterValueNone: NOTHING_KNOWN,
    filterPlaceholder: 'All types',
    render: resource => orUnknown(clusterTypeOf(resource)),
  };
}

function clusterGroupOf(resource: ResourceEntity): string {
  const { group } = resource.spec as { group?: string };
  return group || UNKNOWN;
}

/**
 * The group of a cluster, as the CMDB calls it. The entity carries it as a tag
 * as well, but where it sits among the tags depends on whether the cluster
 * type is known, so it is read off `spec.group`. A slice has no group of its
 * own, so only the table of clusters has the column.
 */
function createClusterGroupColumn(): FilterableColumn<ResourceEntity> {
  return {
    title: 'Group',
    field: 'spec.group',
    filterValueOf: clusterGroupOf,
    filterValueNone: NOTHING_KNOWN,
    filterPlaceholder: 'All groups',
    render: resource => orUnknown(clusterGroupOf(resource)),
  };
}

function instanceOf(resource: ResourceEntity): string {
  const { instance } = resource.spec as { instance?: string };
  return instance || UNKNOWN;
}

/**
 * The cluster management instance an on-prem cluster is run from - Rancher
 * today, which is why the column does not name the tool - and which a slice
 * inherits from the cluster it is cut from. Clusters and slices are named
 * alike whichever instance they are on, so nothing else in the row says
 * where to look for one. A managed cluster has none.
 */
function createInstanceColumn(): FilterableColumn<ResourceEntity> {
  return {
    title: 'Instance',
    field: 'spec.instance',
    filterValueOf: instanceOf,
    filterValueNone: NOTHING_KNOWN,
    filterPlaceholder: 'All instances',
    render: resource => orUnknown(instanceOf(resource)),
  };
}

function productOf(resource: ResourceEntity): string {
  const { product } = resource.spec as { product?: string };
  return product || UNKNOWN;
}

function createProductColumn(): FilterableColumn<ResourceEntity> {
  const none = { value: UNKNOWN, label: 'No Product' };
  return {
    title: 'Product',
    field: 'spec.product',
    filterValueOf: productOf,
    filterValueKind: 'product',
    filterValueNone: none,
    filterPlaceholder: 'All products',
    render: resource => {
      const product = productOf(resource);
      if (product === none.value) {
        return <span style={{ color: '#888' }}>{none.label}</span>;
      }
      return (
        <EntityRefLinkWithVuid
          entityRef={parseEntityRef({ name: product, kind: 'product' })}
          defaultKind="product"
        />
      );
    },
  };
}

function teamOf(resource: ResourceEntity): string {
  const { owner } = resource.spec as { owner?: string };
  return owner || UNKNOWN;
}

/**
 * The team a resource belongs to. It is read off `spec.owner` rather than off
 * the `ownedBy` relation so that a table that fetched its rows itself does not
 * have to fetch their relations as well; both carry the team vuid.
 */
function createTeamColumn(): FilterableColumn<ResourceEntity> {
  const none = { value: UNKNOWN, label: 'No Team' };
  return {
    title: 'Team',
    field: 'spec.owner',
    filterValueOf: teamOf,
    filterValueKind: 'group',
    filterValueNone: none,
    filterPlaceholder: 'All teams',
    render: resource => {
      const team = teamOf(resource);
      if (team === none.value) {
        return <span style={{ color: '#888' }}>{none.label}</span>;
      }
      return (
        <EntityRefLinkWithVuid
          entityRef={parseEntityRef({ name: team, kind: 'group' })}
          defaultKind="group"
        />
      );
    },
  };
}

/**
 * The cluster a slice is cut from, which it carries as its only `deployedIn`
 * entry. Most slices are cut from a cluster of another product, so the column
 * is the only place that says where a slice actually runs; sorting by it puts
 * the slices of one cluster together.
 */
function clusterOf(resource: ResourceEntity): string {
  const { deployedIn } = resource.spec as { deployedIn?: string[] };
  // A slice whose cluster is nowhere in the CMDB keeps no relation to one.
  return deployedIn?.[0] || UNKNOWN;
}

function createClusterColumn(): FilterableColumn<ResourceEntity> {
  return {
    title: 'Cluster',
    field: 'spec.deployedIn',
    filterValueOf: clusterOf,
    filterValueKind: 'resource',
    filterValueNone: NOTHING_KNOWN,
    filterPlaceholder: 'All clusters',
    render: resource => {
      const cluster = clusterOf(resource);
      if (cluster === UNKNOWN) {
        return orUnknown(cluster);
      }
      return (
        <EntityRefLinkWithVuid
          entityRef={parseEntityRef({ name: cluster, kind: 'resource' })}
          defaultKind="resource"
        />
      );
    },
  };
}

/**
 * The id the vendor knows a cloud account by, per resource type: the field of
 * the entity it is read from and what the vendor calls it. The table says
 * which cloud it is, so the column leaves the vendor out of its title.
 *
 * A GCP project shows its project id even though the entity is named after
 * it: the name column shows the title of the project, not the id. Its project
 * number stays on the entity page, where it comes with the warning not to use
 * it, see cloudAccountIdsOf.
 */
const ACCOUNT_ID_COLUMNS: Record<
  string,
  { field: 'accountId'; title: string } | undefined
> = {
  'gcp-project': { field: 'accountId', title: 'Project ID' },
  'aws-account': { field: 'accountId', title: 'Account ID' },
  'azure-subscription': { field: 'accountId', title: 'Subscription ID' },
  'oci-account': { field: 'accountId', title: 'Compartment OCID' },
};

/**
 * The vendor id of a cloud account, see ACCOUNT_ID_COLUMNS. Set by the catalog
 * backend, save for a GCP project the asset inventory has not seen.
 */
function createAccountIdColumn(
  type: string,
): FilterableColumn<ResourceEntity>[] {
  const column = ACCOUNT_ID_COLUMNS[type];
  if (!column) {
    return [];
  }
  const idOf = (resource: ResourceEntity) =>
    (resource.spec as Record<string, unknown>)[column.field] as
      | string
      | undefined;
  return [
    {
      title: column.title,
      field: `spec.${column.field}`,
      render: resource => orUnknown(idOf(resource) || UNKNOWN),
    },
  ];
}

/**
 * The columns of a table of resources. Which of them say something depends on
 * the page the table stands on: a column whose value is the same in every row
 * only repeats what the page already says.
 */
function resourceColumns(options: {
  type: string;
  product: boolean;
  team: boolean;
  cluster: boolean;
  accountId: boolean;
  group: boolean;
  instance: boolean;
}): FilterableColumn<ResourceEntity>[] {
  return [
    createEntityNameColumn<ResourceEntity>('resource'),
    ...(options.accountId ? createAccountIdColumn(options.type) : []),
    // Only kubernetes carries one - a slice inherits it from the cluster it is
    // cut from - while a cloud account says what runs it by its resource type.
    ...(KUBERNETES_TYPES.includes(options.type)
      ? [createClusterTypeColumn()]
      : []),
    ...(options.instance && KUBERNETES_TYPES.includes(options.type)
      ? [createInstanceColumn()]
      : []),
    ...(options.group && options.type === 'k8s-cluster'
      ? [createClusterGroupColumn()]
      : []),
    ...(options.product ? [createProductColumn()] : []),
    ...(options.team ? [createTeamColumn()] : []),
    ...(options.cluster ? [createClusterColumn()] : []),
    createEnvironmentColumn(),
  ];
}

// The org tree below a department is walked in the browser, so ask for the
// fields that build it and nothing else.
const GROUP_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.title',
  'metadata.tags',
  'spec.type',
  'spec.parent',
];

const RESOURCE_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.tags',
  'spec.type',
  'spec.owner',
  'spec.product',
  'spec.deployedIn',
  'spec.clusterType',
  'spec.instance',
];

// A department at the top of the tree has more teams below it than belong into
// one query string, so the owners are asked for a batch at a time.
const OWNERS_PER_QUERY = 50;

function inBatches<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size));
  }
  return batches;
}

/**
 * How long an answer is handed to whoever asks next before it is fetched
 * again.
 *
 * A department tab carries several of these cards and every one of them needs
 * the same handful of things - the org tree, the resources below the
 * department, the products that have been retired - which used to be three
 * queries per card, all in flight at once and all queueing behind each other.
 * The catalog behind them is refreshed on the order of minutes, so a minute of
 * holding on to an answer costs nothing in freshness and takes a tab from two
 * dozen queries down to five. Switching between the Kubernetes and the Cloud
 * Accounts tabs then costs none at all.
 */
const CACHE_TTL_MS = 60_000;

type Cached = { at: number; answer: Promise<unknown> };

/**
 * The answers already given, per catalog api. They hang off the api rather
 * than off this module so that a test, which stands up a catalog of its own
 * for each case, never gets handed what another one asked for.
 */
const answers = new WeakMap<CatalogApi, Map<string, Cached>>();

function cached<T>(
  catalogApi: CatalogApi,
  key: string,
  ask: () => Promise<T>,
): Promise<T> {
  let byKey = answers.get(catalogApi);
  if (!byKey) {
    byKey = new Map();
    answers.set(catalogApi, byKey);
  }
  const given = byKey.get(key);
  if (given && Date.now() - given.at < CACHE_TTL_MS) {
    return given.answer as Promise<T>;
  }
  const answer = ask();
  byKey.set(key, { at: Date.now(), answer: answer });
  // A query that failed is worth trying again, rather than handing the same
  // error to everyone who asks over the next minute.
  const forKey = byKey;
  answer.catch(() => {
    if (forKey.get(key)?.answer === answer) {
      forKey.delete(key);
    }
  });
  return answer;
}

/**
 * Every department and team of the org tree. The whole tree comes back
 * whichever department is asking - it is walked in the browser - so it is
 * fetched once and shared.
 */
function orgGroups(catalogApi: CatalogApi): Promise<Entity[]> {
  return cached(catalogApi, 'org-groups', async () => {
    const groups = await catalogApi.getEntities({
      filter: [
        { kind: 'Group', 'spec.type': 'department' },
        { kind: 'Group', 'spec.type': 'team' },
      ],
      fields: GROUP_FIELDS,
    });
    return groups.items;
  });
}

/**
 * The vuids of the teams below a department, those of its sub departments
 * among them. An archived team is left out of the org tree, and so is
 * everything it owns.
 */
async function teamsBelow(
  catalogApi: CatalogApi,
  department: string,
): Promise<string[]> {
  return subtreeOf(buildOrgRows(await orgGroups(catalogApi)), department)
    .filter(row => !row.isDepartment)
    .map(row => row.id);
}

/**
 * The resource types a department page has a tab for, which is everything it
 * ever asks the catalog for.
 *
 * The CMDB is not the only thing that puts resources in the catalog - the GCP
 * asset inventory adds namespaces, buckets, topics and more, by the
 * thousand - so the query names the types it wants rather than asking for
 * whatever the teams happen to own.
 */
const DEPARTMENT_RESOURCE_TYPES = [...KUBERNETES_TYPES, ...CLOUD_TYPES];

/** The same empty array every time, so that a render is not a change of rows. */
const NO_RESOURCES: ResourceEntity[] = [];

/**
 * Everything below a department that any of its tabs shows, in one go.
 *
 * A department owns nothing itself - a cluster or an account belongs to a
 * team - so there is no relation to follow here the way there is on a team or
 * a product page. The teams are read off the org tree and the resources are
 * then asked for by owner, a batch of owners at a time.
 *
 * All the types come back together rather than one query per card. The cards
 * of a tab then share a single answer, both tabs share it with each other, and
 * so does the question of which tabs to show at all - which used to be a facet
 * query and was by far the slowest thing on the page.
 */
function useDepartmentResources(department: string) {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading, error } = useAsync(
    () =>
      cached(catalogApi, `resources:${department}`, async () => {
        const teams = await teamsBelow(catalogApi, department);
        if (teams.length === 0) {
          return NO_RESOURCES;
        }
        const batches = await Promise.all(
          inBatches(teams, OWNERS_PER_QUERY).map(owners =>
            catalogApi.getEntities({
              filter: {
                kind: 'Resource',
                'spec.type': DEPARTMENT_RESOURCE_TYPES,
                'spec.owner': owners,
              },
              fields: RESOURCE_FIELDS,
            }),
          ),
        );
        return batches.flatMap(batch => batch.items) as ResourceEntity[];
      }),
    [catalogApi, department],
  );

  return { resources: value ?? NO_RESOURCES, loading: loading, error: error };
}

export interface DepartmentResourceTypes {
  /**
   * The kinds of resource the department holds: `k8s-cluster`, `gcp-project`,
   * and so on. Empty while `loading` is still true.
   */
  types: Set<string>;
  /** Whether the answer is still out. */
  loading: boolean;
}

/**
 * The kinds of resource a department holds, gathered over every team below it.
 *
 * A page asks this to leave out a tab that would only carry empty cards. The
 * answer is read off the rows the cards of those tabs are built from - see
 * `useDepartmentResources` - so asking it costs the page nothing beyond what
 * the cards fetch anyway.
 *
 * `loading` comes back with the set because the two answers mean different
 * things to a page. An empty set while loading is "not known yet", and a page
 * that hides a tab on it tells the reader there is no data when it does not
 * know that. Once loading is false, an empty set really is nothing held.
 */
export function useDepartmentResourceTypes(
  department: string,
): DepartmentResourceTypes {
  const { resources, loading } = useDepartmentResources(department);

  const types = React.useMemo(
    () => new Set(resources.map(resource => resource.spec.type as string)),
    [resources],
  );

  return { types: types, loading: loading };
}

/**
 * The kinds of resource a team holds, the team page's answer to the same
 * question `useDepartmentResourceTypes` answers for a department - and with
 * the same meaning of `loading`.
 *
 * The relations of the team cannot answer it: they say that the team owns a
 * resource, but not of which type, and a cloud account is named after its
 * provider id rather than after its type. Only the types are asked for, the
 * cards fetch the rows themselves.
 */
export function useTeamResourceTypes(team: string): DepartmentResourceTypes {
  const catalogApi = useApi(catalogApiRef);

  const { value, loading } = useAsync(
    () =>
      cached(catalogApi, `resource-types:${team}`, async () => {
        const resources = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': DEPARTMENT_RESOURCE_TYPES,
            'spec.owner': team,
          },
          fields: ['spec.type'],
        });
        return new Set(
          resources.items.map(resource => resource.spec?.type as string),
        );
      }),
    [catalogApi, team],
  );

  return { types: value ?? NO_TYPES, loading: loading };
}

/** The same empty set every time, see `NO_RESOURCES`. */
const NO_TYPES = new Set<string>();

/**
 * Whether the product behind a resource has been retired.
 *
 * A cluster or an account carries no tag of its own - the environment is what
 * its tags say - so the only thing that makes it archived is the product it
 * belongs to. The vuids of the retired products are asked for once and then
 * shared by every card on the page: the catalog is given the tag to match, so
 * what comes back is the handful of retired ones rather than every product
 * there is, and of those only the name.
 *
 * Until the answer is in nothing is held back. It is one small query next to
 * the one that fetches the rows themselves and settles first in practice, and
 * a row that appears and then goes is the lesser of the two wrongs - the other
 * is a table that keeps its rows waiting on a question about their products.
 */
function useIsOfArchivedProduct(): (resource: ResourceEntity) => boolean {
  const catalogApi = useApi(catalogApiRef);

  const { value } = useAsync(
    () =>
      cached(catalogApi, 'archived-products', async () => {
        const products = await catalogApi.getEntities({
          filter: { kind: 'Product', 'metadata.tags': ARCHIVED_TAG },
          fields: ['metadata.name'],
        });
        return new Set(products.items.map(product => product.metadata.name));
      }),
    [catalogApi],
  );

  return React.useCallback(
    (resource: ResourceEntity) => value?.has(productOf(resource)) ?? false,
    [value],
  );
}

/**
 * The resources of one type of every team below a department, picked out of
 * the rows the whole department was fetched as - see
 * `useDepartmentResources`, which every card on the tab shares.
 */
const DepartmentResourcesCard = (
  props: HasFilteredResourcesCardProps & {
    isArchived: (resource: ResourceEntity) => boolean;
  },
) => {
  const { variant = 'gridItem', type, title, isArchived } = props;
  const { entity } = useEntity();
  const { resources, loading, error } = useDepartmentResources(
    entity.metadata.name,
  );

  // Hold on to the same rows for as long as they are the same rows: the table
  // below keeps its paging and its filters against the array it was handed.
  const value = React.useMemo(
    () => resources.filter(resource => resource.spec.type === type),
    [resources, type],
  );

  if (loading) {
    return (
      <InfoCard variant={variant} title={title}>
        <Progress />
      </InfoCard>
    );
  }
  if (error) {
    return (
      <InfoCard variant={variant} title={title}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  // A card with nothing in it is left out, the way the relation driven cards
  // of a team page leave themselves out. The tab it sits on stays either way:
  // whether a department holds anything only comes out of the query above,
  // while the `if` of a route is answered from the entity alone.
  if (!value || value.length === 0) {
    return <></>;
  }

  // Rows from all over the department: the product and the team say which
  // corner of it each one comes from.
  const columns = resourceColumns({
    type: type,
    product: true,
    team: true,
    cluster: type === 'k8s-cluster-slice',
    // A department lists the accounts of a whole corner of the org, where the
    // vendor ids are only noise; they are for the team and the product that
    // hold the accounts.
    accountId: false,
    // Likewise the cluster group, which only means something to the team and
    // the product that run the clusters.
    group: false,
    // Unlike the instance, which is where anybody goes to look at one.
    instance: true,
  });

  return (
    <EntityFilterTable
      title={title}
      variant={variant}
      columns={columns}
      entities={value}
      isArchived={isArchived}
      countInTitle
      tableOptions={{ thirdSortClick: false, ...FILTER_TABLE_OPTIONS }}
    />
  );
};

export const HasFilteredResourcesCard = (
  props: HasFilteredResourcesCardProps,
) => {
  const { variant = 'gridItem' } = props;
  const { entity } = useEntity();
  // Asked for here rather than in either branch below, so that the hook runs
  // on every page the card stands on.
  const isArchived = useIsOfArchivedProduct();

  if (entity.spec?.type === 'department') {
    return <DepartmentResourcesCard {...props} isArchived={isArchived} />;
  }

  const relationType =
    entity.kind === 'Group' ? RELATION_OWNER_OF : RELATION_HOSTS;

  const resourceEntityColumns = resourceColumns({
    type: props.type,
    // A product page lists what belongs to that one product, so the column
    // would repeat the page. Everywhere else the rows come from several
    // products: a team mixes its own, a cluster is sliced up for others.
    product: entity.kind !== 'Product',
    // A team page fixes the team to the page and a product page to the team of
    // the product, so the team is only worth a column on the page of a
    // resource - a cluster sliced up for the teams of other products.
    team: entity.kind === 'Resource',
    // On the page of the cluster itself every row names that same cluster.
    cluster:
      props.type === 'k8s-cluster-slice' && entity.spec?.type !== 'k8s-cluster',
    // The team and the product the accounts belong to, see the department
    // card for why only those.
    accountId: entity.kind === 'Group' || entity.kind === 'Product',
    group: entity.kind === 'Group' || entity.kind === 'Product',
    // The slices of a cluster are all on the instance of that cluster, which
    // its page already names.
    instance: entity.spec?.type !== 'k8s-cluster',
  });

  const asResourceEntities = (entities: Entity[]): ResourceEntity[] =>
    entities.filter(e => e?.spec?.type === props.type) as ResourceEntity[];

  return (
    <RelatedEntitiesCard
      variant={variant}
      title={props.title}
      entityKind="Resource"
      relationType={relationType}
      columns={resourceEntityColumns}
      asRenderableEntities={asResourceEntities}
      isArchived={isArchived}
      countInTitle
      tableOptions={FILTER_TABLE_OPTIONS}
    />
  );
};
